import { BaseElement } from 'base';

const MS_PER_DAY = 86400000;

function toISO(date) {
  return date.toISOString().slice(0, 10);
}

function fromISO(iso) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function todayUTC() {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

class DatePicker extends BaseElement {
  #handlers = new Set();

  onConnect() {
    this.hiddenInput = this.q('input[type="hidden"]');
    this.monthSelect = this.q('[data-date-picker-month]');
    this.yearSelect = this.q('[data-date-picker-year]');
    this.monthLabel = this.q('[data-date-picker-month-label]');
    this.yearLabel = this.q('[data-date-picker-year-label]');
    this.weeksBody = this.q('[data-date-picker-weeks]');
    this.prevButton = this.q('[data-date-picker-prev]');
    this.nextButton = this.q('[data-date-picker-next]');

    if (
      !this.monthSelect ||
      !this.yearSelect ||
      !this.weeksBody ||
      !this.prevButton ||
      !this.nextButton
    ) {
      return;
    }

    this.lang = document.documentElement.lang || 'en';
    this.isRtl = getComputedStyle(this).direction === 'rtl';
    this.firstDay = Number(this.dataset.firstDay) || 0;
    this.monthFormatter = new Intl.DateTimeFormat(this.lang, {
      month: 'short',
      timeZone: 'UTC'
    });
    this.weekdayFormatter = new Intl.DateTimeFormat(this.lang, {
      weekday: 'short',
      timeZone: 'UTC'
    });
    this.dayLabelFormatter = new Intl.DateTimeFormat(this.lang, {
      dateStyle: 'long',
      timeZone: 'UTC'
    });

    this.minDate = this.dataset.min ? fromISO(this.dataset.min) : null;
    this.maxDate = this.dataset.max ? fromISO(this.dataset.max) : null;
    this.selectedISO = this.dataset.value || null;

    const initial = this.selectedISO ? fromISO(this.selectedISO) : todayUTC();
    this.viewYear = initial.getUTCFullYear();
    this.viewMonth = initial.getUTCMonth();

    this.localizeWeekdays();
    this.buildYearOptions();
    this.buildMonthOptions();
    this.render();

    this.listen(this.prevButton, 'click', () => this.shiftMonth(-1));
    this.listen(this.nextButton, 'click', () => this.shiftMonth(1));
    this.listen(this.monthSelect, 'change', this.onMonthChange);
    this.listen(this.yearSelect, 'change', this.onYearChange);
    this.listen(this.weeksBody, 'click', this.onDayClick);
    this.listen(this.weeksBody, 'keydown', this.onDayKeydown);
  }

  get isOpen() {
    return !this.hidden;
  }

  open() {
    this.hidden = false;
  }

  close() {
    this.hidden = true;
  }

  toggle() {
    this.hidden = !this.hidden;
  }

  localizeWeekdays() {
    this.weekdayColumns = [];
    for (const [index, th] of this.qa('.date-picker__weekday').entries()) {
      const dayOfWeek = (this.firstDay + index) % 7;
      const fullLabel = this.weekdayFormatter.format(
        new Date(Date.UTC(2025, 0, 5 + dayOfWeek))
      );
      const label = fullLabel.slice(0, 2);
      th.textContent = label;
      th.setAttribute('abbr', fullLabel);
      this.weekdayColumns[dayOfWeek] = th;
    }
    this.updateWeekdayHighlight();
  }

  updateWeekdayHighlight() {
    if (!this.weekdayColumns) return;
    const highlighted = this.selectedISO
      ? fromISO(this.selectedISO).getUTCDay()
      : todayUTC().getUTCDay();
    for (const [dayOfWeek, th] of this.weekdayColumns.entries()) {
      if (dayOfWeek === highlighted) th.setAttribute('data-today', 'true');
      else th.removeAttribute('data-today');
    }
  }

  buildMonthOptions() {
    this.monthSelect.textContent = '';
    for (let month = 0; month < 12; month += 1) {
      const option = document.createElement('option');
      option.value = String(month);
      option.textContent = this.monthFormatter.format(
        new Date(Date.UTC(2025, month, 1))
      );
      this.monthSelect.append(option);
    }
  }

  buildYearOptions() {
    const currentYear = new Date().getUTCFullYear();
    const minYear = this.minDate
      ? this.minDate.getUTCFullYear()
      : currentYear - 100;
    const maxYear = this.maxDate
      ? this.maxDate.getUTCFullYear()
      : currentYear + 10;
    this.yearSelect.textContent = '';
    for (let year = minYear; year <= maxYear; year += 1) {
      const option = document.createElement('option');
      option.value = String(year);
      option.textContent = String(year);
      this.yearSelect.append(option);
    }
  }

  isOutOfRange(date) {
    if (this.minDate && date < this.minDate) return true;
    if (this.maxDate && date > this.maxDate) return true;
    return false;
  }

  syncSelect(select, value) {
    for (const option of select.options)
      option.defaultSelected = option.value === value;
    select.value = value;
  }

  render() {
    this.syncSelect(this.monthSelect, String(this.viewMonth));
    this.syncSelect(this.yearSelect, String(this.viewYear));
    if (this.monthLabel)
      this.monthLabel.textContent = this.monthSelect.selectedOptions[0]?.text;
    if (this.yearLabel)
      this.yearLabel.textContent = this.yearSelect.selectedOptions[0]?.text;

    const firstOfMonth = new Date(Date.UTC(this.viewYear, this.viewMonth, 1));
    const leadingBlanks = (firstOfMonth.getUTCDay() - this.firstDay + 7) % 7;
    const daysInMonth = new Date(
      Date.UTC(this.viewYear, this.viewMonth + 1, 0)
    ).getUTCDate();
    const weeksInMonth = Math.ceil((leadingBlanks + daysInMonth) / 7);
    const renderedCells = weeksInMonth * 7;

    for (const [index, row] of this.qa('.date-picker__week').entries())
      row.hidden = index >= weeksInMonth;

    const dayButtons = this.qa('[data-date-picker-day]');
    let focusCandidate = null;

    dayButtons.forEach((button, index) => {
      if (index >= renderedCells) {
        button.textContent = '';
        button.tabIndex = -1;
        for (const attribute of [
          'data-date',
          'data-disabled',
          'aria-disabled',
          'aria-pressed',
          'aria-label'
        ])
          button.removeAttribute(attribute);
        return;
      }

      const cellDate = new Date(
        firstOfMonth.getTime() + (index - leadingBlanks) * MS_PER_DAY
      );
      const iso = toISO(cellDate);
      const outOfRange = this.isOutOfRange(cellDate);
      const isToday = iso === toISO(todayUTC());
      const isSelected = iso === this.selectedISO;

      button.textContent = String(cellDate.getUTCDate());
      button.dataset.date = iso;
      button.setAttribute(
        'aria-label',
        this.dayLabelFormatter.format(cellDate)
      );
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.tabIndex = -1;

      if (outOfRange) {
        button.setAttribute('data-disabled', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('data-disabled');
        button.removeAttribute('aria-disabled');
      }

      if (!outOfRange && (isSelected || (isToday && !focusCandidate)))
        focusCandidate = button;
    });

    const roving = focusCandidate || dayButtons[leadingBlanks] || dayButtons[0];
    if (roving) roving.tabIndex = 0;
  }

  shiftMonth(delta) {
    this.viewMonth += delta;
    if (this.viewMonth < 0) {
      this.viewMonth = 11;
      this.viewYear -= 1;
    } else if (this.viewMonth > 11) {
      this.viewMonth = 0;
      this.viewYear += 1;
    }
    this.render();
  }

  onMonthChange() {
    this.viewMonth = Number(this.monthSelect.value);
    this.render();
  }

  onYearChange() {
    this.viewYear = Number(this.yearSelect.value);
    this.render();
  }

  onDayClick(event) {
    const button = event.target.closest('[data-date-picker-day]');
    if (button && button.dataset.date && !button.hasAttribute('data-disabled'))
      this.pick(button.dataset.date);
  }

  onDayKeydown(event) {
    const current = event.target.closest('[data-date-picker-day]');
    if (!current || !current.dataset.date) return;

    const dayDeltas = {
      ArrowLeft: this.isRtl ? 1 : -1,
      ArrowRight: this.isRtl ? -1 : 1,
      ArrowUp: -7,
      ArrowDown: 7
    };

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!current.hasAttribute('data-disabled'))
        this.pick(current.dataset.date);
      return;
    }

    if (event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      const dayOfMonth = fromISO(current.dataset.date).getUTCDate();
      this.shiftMonth(event.key === 'PageUp' ? -1 : 1);
      const daysInNewMonth = new Date(
        Date.UTC(this.viewYear, this.viewMonth + 1, 0)
      ).getUTCDate();
      const targetDay = Math.min(dayOfMonth, daysInNewMonth);
      this.focusDate(
        toISO(new Date(Date.UTC(this.viewYear, this.viewMonth, targetDay)))
      );
      return;
    }

    if (
      event.key !== 'Home' &&
      event.key !== 'End' &&
      !(event.key in dayDeltas)
    )
      return;
    event.preventDefault();

    const currentDate = fromISO(current.dataset.date);
    const offsetFromWeekStart =
      (currentDate.getUTCDay() - this.firstDay + 7) % 7;
    let targetDate;
    if (event.key === 'Home') {
      targetDate = new Date(
        currentDate.getTime() - offsetFromWeekStart * MS_PER_DAY
      );
    } else if (event.key === 'End') {
      targetDate = new Date(
        currentDate.getTime() + (6 - offsetFromWeekStart) * MS_PER_DAY
      );
    } else {
      targetDate = new Date(
        currentDate.getTime() + dayDeltas[event.key] * MS_PER_DAY
      );
    }

    if (
      targetDate.getUTCFullYear() !== this.viewYear ||
      targetDate.getUTCMonth() !== this.viewMonth
    ) {
      this.viewYear = targetDate.getUTCFullYear();
      this.viewMonth = targetDate.getUTCMonth();
      this.render();
    }
    this.focusDate(toISO(targetDate));
  }

  focusRoving() {
    const roving = this.qa('[data-date-picker-day]').find(
      button => button.tabIndex === 0
    );
    roving?.focus();
  }

  focusDate(iso) {
    const button = this.qa('[data-date-picker-day]').find(
      candidate => candidate.dataset.date === iso
    );
    if (!button) return;
    for (const other of this.qa('[data-date-picker-day]')) other.tabIndex = -1;
    button.tabIndex = 0;
    button.focus();
  }

  pick(iso) {
    const hadFocus = this.contains(document.activeElement);
    this.selectedISO = iso;
    this.updateWeekdayHighlight();
    if (this.hiddenInput) {
      this.hiddenInput.value = iso;
      this.hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const pickedDate = fromISO(iso);
    if (
      pickedDate.getUTCFullYear() !== this.viewYear ||
      pickedDate.getUTCMonth() !== this.viewMonth
    ) {
      this.viewYear = pickedDate.getUTCFullYear();
      this.viewMonth = pickedDate.getUTCMonth();
      if (hadFocus) this.nextButton.focus({ preventScroll: true });
      this.render();
      if (hadFocus) this.focusDate(iso);
    } else {
      const liveButtons = this.qa('[data-date-picker-day]').filter(
        button => button.dataset.date
      );
      for (const button of liveButtons)
        button.setAttribute(
          'aria-pressed',
          button.dataset.date === iso ? 'true' : 'false'
        );
    }
    this.#emit({ id: this.id, value: iso });
  }

  onChange(handler) {
    this.#handlers.add(handler);
    return () => this.#handlers.delete(handler);
  }

  onDisconnect() {
    this.#handlers.clear();
  }

  #emit(detail) {
    for (const handler of [...this.#handlers]) {
      try {
        handler(detail);
      } catch (error) {
        console.error('[date-picker] change handler failed:', error);
      }
    }
  }
}

if (!customElements.get('date-picker'))
  customElements.define('date-picker', DatePicker);
